import { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, ChevronRight, Search, Moon, BookOpen, History, Activity, Download, X, Calendar } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import TechnicalGuide from "@/components/TechnicalGuide";

const LEVEL_COLOR = { 0: "#6B7280", 1: "#0A0A0A", 2: "#DC2626" };

export default function Alerts() {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [filter, setFilter] = useState("all");
    const [tab, setTab] = useState("active");   // "active" | "today" | "history"
    const [category, setCategory] = useState("all"); // "all" | "hardware" | "grid" | "communication" | "performance"
    const [expandedGuide, setExpandedGuide] = useState({});
    const [showDownload, setShowDownload] = useState(false);
    const [plants, setPlants] = useState([]);
    const { user } = useAuth();
    const canDownload = user?.role === "super_admin" || user?.role === "sales";
    const nav = useNavigate();

    useEffect(() => {
        if (canDownload) {
            api.get("/plants").then(r => setPlants(r.data || [])).catch(() => {});
        }
    }, [canDownload]);

    useEffect(() => {
        setLoading(true);
        let endpoint = "/alerts";
        if (tab === "history") endpoint = "/alerts/history";
        else if (tab === "today") endpoint = "/alerts/history?scope=today";
        api.get(endpoint)
            .then(r => setAlerts(r.data))
            .finally(() => setLoading(false));
    }, [tab]);

    let list = alerts;
    if (category !== "all") list = list.filter(a => a.category === category);
    // The "In-Hours Only" filter hides alerts raised outside solar operating
    // hours (night-time inverter shutdowns) — regardless of tab.
    if (filter === "hours") list = list.filter(a => a.is_operational_hours);
    if (q) list = list.filter(a =>
        a.alertName?.toLowerCase().includes(q.toLowerCase()) ||
        a.protocolName?.toLowerCase().includes(q.toLowerCase()) ||
        a.stationName?.toLowerCase().includes(q.toLowerCase()) ||
        a.deviceSn?.includes(q)
    );

    const activeCount = alerts.filter(a => a.is_active !== false && !a.alertEndTime).length;
    const closedCount = alerts.filter(a => a.alertEndTime).length;
    const offHoursCount = alerts.filter(a => a.is_operational_hours === false).length;

    return (
        <div className="space-y-6" data-testid="alerts-page">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Fault Center</div>
                    <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900 mt-1">
                        {tab === "history" ? `Alert History (${alerts.length})` : tab === "today" ? `Today's Alerts (${alerts.length})` : `Active Alerts (${activeCount})`}
                    </h1>
                    <p className="text-sm text-[#6B7280] mt-1">
                        {tab === "history"
                            ? "Last 60 days of SolarOS fault activity — active and cleared. Click any row for the plant + inverter context."
                            : tab === "today"
                            ? "Every alert raised on the current IST day — active and cleared. Fresh on every visit; auto-refreshes with the latest synthetic offline detections."
                            : "All inverter-level active alarms across the fleet. Click any alert to open its detail page with plant, inverter and telemetry context."}
                    </p>
                </div>
                <div className="text-xs font-mono flex items-center gap-3">
                    <span className="text-[#DC2626]" data-testid="active-alerts-count">{activeCount} ACTIVE</span>
                    {(tab === "history" || tab === "today") && closedCount > 0 && (
                        <span className="text-[#16A34A]" data-testid="closed-alerts-count">{closedCount} CLEARED</span>
                    )}
                    {offHoursCount > 0 && (
                        <span className="flex items-center gap-1 text-[#6B7280]" title="Alerts raised outside solar operating hours (05:30 – 18:00 IST)" data-testid="off-hours-summary">
                            <Moon className="w-3 h-3" /> {offHoursCount} OFF-HOURS
                        </span>
                    )}
                </div>
            </div>

            {/* Active / History tabs + Download (admin/sales only) */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="inline-flex rounded-md border border-[#E5E7EB] overflow-hidden bg-white">
                    {[
                        { k: "active", l: "Active", icon: Activity },
                        { k: "today", l: "Today", icon: Calendar },
                        { k: "history", l: "History · 60d", icon: History },
                    ].map(t => (
                        <button
                            key={t.k}
                            onClick={() => setTab(t.k)}
                            data-testid={`alerts-tab-${t.k}`}
                            className={`px-4 h-9 text-xs uppercase tracking-wider font-medium transition-colors duration-200 flex items-center gap-1.5 ${tab === t.k ? "bg-[#0B0D11] text-white" : "bg-white text-[#6B7280] hover:bg-[#F9FAFB]"}`}
                        >
                            <t.icon className="w-3 h-3" /> {t.l}
                        </button>
                    ))}
                </div>
                {canDownload && (
                    <Button
                        onClick={() => setShowDownload(true)}
                        variant="outline"
                        className="rounded-md h-9 text-xs uppercase tracking-wider border-[#E5E7EB]"
                        data-testid="alerts-download-btn"
                    >
                        <Download className="w-3.5 h-3.5 mr-1.5" /> Download
                    </Button>
                )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 max-w-sm">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
                    <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search by code, name, plant, SN…" className="pl-9 rounded-md bg-white" data-testid="alerts-search" />
                </div>
                <div className="inline-flex rounded-md border border-[#E5E7EB] overflow-hidden bg-white">
                    {[{ k: "all", l: "All" }, { k: "hours", l: "In-Hours Only" }].map(t => (
                        <button
                            key={t.k}
                            onClick={() => setFilter(t.k)}
                            data-testid={`filter-${t.k}`}
                            className={`px-3 h-9 text-xs uppercase tracking-wider font-medium transition-colors duration-200 ${filter === t.k ? "bg-[#0B0D11] text-white" : "bg-white text-[#6B7280] hover:bg-[#F9FAFB]"}`}
                        >
                            {t.l}
                        </button>
                    ))}
                </div>
            </div>

            {/* Category chips: Hardware · Grid · Communication · Performance */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="alert-category-chips">
                {[
                    { k: "all", l: "All Categories" },
                    { k: "hardware", l: "Hardware" },
                    { k: "grid", l: "Grid" },
                    { k: "communication", l: "Communication" },
                    { k: "performance", l: "Performance" },
                ].map(c => {
                    const count = c.k === "all" ? alerts.length : alerts.filter(a => a.category === c.k).length;
                    const active = category === c.k;
                    return (
                        <button
                            key={c.k}
                            onClick={() => setCategory(c.k)}
                            data-testid={`alert-cat-${c.k}`}
                            className={`inline-flex items-center gap-1.5 px-3 h-8 rounded-full text-xs uppercase tracking-wider font-medium transition-colors ${active ? "bg-[#0A0A0A] text-white border border-[#0A0A0A]" : "bg-white text-[#6B7280] border border-[#E5E7EB] hover:bg-[#F9FAFB]"}`}
                        >
                            {c.l}
                            <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded-full ${active ? "bg-white/25 text-white" : "bg-zinc-100 text-zinc-600"}`}>{count}</span>
                        </button>
                    );
                })}
            </div>

            {loading ? <div className="text-sm text-[#6B7280]">Loading alerts…</div> : list.length === 0 ? (
                <div className="bg-white border border-[#E5E7EB] rounded-lg p-10 text-center">
                    <div className="text-sm text-[#16A34A] font-medium">No alerts match your view.</div>
                </div>
            ) : (
                <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
                    <div className="overflow-x-auto">
                    <table className="w-full text-sm min-w-[860px]">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-wider text-[#6B7280] bg-[#F9FAFB] border-b border-[#E5E7EB]">
                                <th className="py-2.5 px-4 text-left font-semibold">Code</th>
                                <th className="py-2.5 px-4 text-left font-semibold">Name</th>
                                <th className="py-2.5 px-4 text-left font-semibold">Plant</th>
                                <th className="py-2.5 px-4 text-left font-semibold">Device SN</th>
                                <th className="py-2.5 px-4 text-left font-semibold">Started</th>
                                <th className="py-2.5 px-4 text-left font-semibold">Status</th>
                                <th className="py-2.5 px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map((a, i) => {
                                const color = LEVEL_COLOR[a.level] || "#0A0A0A";
                                const isGuideOpen = !!expandedGuide[a.alertId];
                                const rows = [
                                    <tr
                                        key={a.alertId}
                                        onClick={() => nav(`/alerts/${a.alertId}`)}
                                        className={`border-b border-[#F3F4F6] hover:bg-[#F9FAFB] cursor-pointer transition-colors duration-200 group ${a.is_operational_hours === false ? "opacity-60" : ""}`}
                                        data-testid={`alert-row-${i}`}
                                    >
                                        <td className="py-3 px-4">
                                            <span className="inline-flex items-center gap-1 text-xs font-mono px-1.5 py-0.5 rounded-sm" style={{ background: `${color}12`, color, border: `1px solid ${color}44` }}>
                                                <AlertTriangle className="w-3 h-3" />{a.protocolName}
                                            </span>
                                        </td>
                                        <td className="py-3 px-4 text-[#111827] group-hover:text-[#0A0A0A] transition-colors font-medium">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span>{a.alertName}</span>
                                                {a.category && (
                                                    <span className={`inline-flex items-center text-[10px] font-mono px-1.5 py-0.5 rounded-sm uppercase tracking-wider ${
                                                        a.category === "communication" ? "bg-zinc-100 text-zinc-700 border border-zinc-200" :
                                                        a.category === "grid" ? "bg-blue-50 text-blue-700 border border-blue-200" :
                                                        a.category === "performance" ? "bg-purple-50 text-purple-700 border border-purple-200" :
                                                        "bg-red-50 text-red-700 border border-red-200"
                                                    }`} data-testid={`alert-cat-badge-${i}`}>
                                                        {a.category}
                                                    </span>
                                                )}
                                                {a.is_synthetic && (
                                                    <span className="inline-flex items-center text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-orange-50 text-orange-700 border border-orange-200" title="Derived from station/device telemetry — SolarOS didn't emit a specific alert code">
                                                        DERIVED
                                                    </span>
                                                )}
                                                {a.is_operational_hours === false && (
                                                    <span className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-[#F3F4F6] text-[#6B7280] border border-[#E5E7EB]" title="Alert raised outside solar operating hours (05:30 – 18:00 IST) — usually inverter night shutdown" data-testid={`off-hours-badge-${i}`}>
                                                        <Moon className="w-2.5 h-2.5" /> OFF-HOURS
                                                    </span>
                                                )}
                                                {a.technical_guide && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); setExpandedGuide(m => ({ ...m, [a.alertId]: !m[a.alertId] })); }}
                                                        className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                                                        data-testid={`guide-toggle-${i}`}
                                                    >
                                                        <BookOpen className="w-2.5 h-2.5" /> Guide {a.technical_guide.code}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                        <td className="py-3 px-4">
                                            <Link
                                                to={`/plants/${a.stationId}`}
                                                onClick={(e) => e.stopPropagation()}
                                                className="text-[#6B7280] hover:text-[#0A0A0A]"
                                            >
                                                <div className="font-medium text-[#111827]">{a.customer_name || a.stationName}</div>
                                                {a.customer_name && a.location && (
                                                    <div className="text-[10px] font-mono text-[#9CA3AF] mt-0.5">{a.location}</div>
                                                )}
                                            </Link>
                                        </td>
                                        <td className="py-3 px-4 font-mono text-xs text-[#6B7280]">{a.deviceSn || <span className="text-zinc-400 italic">station-wide</span>}</td>
                                        <td className="py-3 px-4 font-mono text-xs text-[#6B7280]">{new Date(a.alertStartTime * 1000).toLocaleString()}</td>
                                        <td className="py-3 px-4">
                                            {a.alertEndTime ? (
                                                <span className="text-xs font-mono text-[#16A34A]" title={`Cleared at ${new Date(a.alertEndTime * 1000).toLocaleString()}`}>✓ CLEARED</span>
                                            ) : (
                                                <span className="text-xs font-mono text-[#DC2626]">● ACTIVE</span>
                                            )}
                                        </td>
                                        <td className="py-3 px-4 text-right">
                                            <ChevronRight className="w-4 h-4 text-[#9CA3AF] group-hover:text-[#0A0A0A] transition-colors" />
                                        </td>
                                    </tr>
                                ];
                                if (isGuideOpen && a.technical_guide) {
                                    rows.push(
                                        <tr key={`${a.alertId}-guide`} className="bg-blue-50/30 border-b border-[#F3F4F6]" data-testid={`guide-row-${i}`}>
                                            <td colSpan={7} className="p-3">
                                                <TechnicalGuide guide={a.technical_guide} testid={`alert-guide-${i}`} />
                                            </td>
                                        </tr>
                                    );
                                }
                                return rows;
                            })}
                        </tbody>
                    </table>
                    </div>
                </div>
            )}
            {showDownload && (
                <DownloadDialog plants={plants} onClose={() => setShowDownload(false)} />
            )}
        </div>
    );
}

function DownloadDialog({ plants, onClose }) {
    const uniqueAlerts = ["", "F35", "F42", "F48", "F41", "F5.11", "AC_NoUtility", "AC_UnderFreq"];
    const today = new Date().toISOString().slice(0, 10);
    const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toISOString().slice(0, 10); };
    const [stationId, setStationId] = useState("");
    const [protocolName, setProtocolName] = useState("");
    const [alertName, setAlertName] = useState("");
    const [status, setStatus] = useState("");
    const [startDate, setStartDate] = useState(daysAgo(30));
    const [endDate, setEndDate] = useState(today);
    const [downloading, setDownloading] = useState(false);

    const applyPreset = (days) => {
        if (days === "all") { setStartDate(""); setEndDate(""); return; }
        setStartDate(daysAgo(days));
        setEndDate(today);
    };

    const download = async () => {
        setDownloading(true);
        try {
            const params = new URLSearchParams();
            if (stationId) params.set("station_id", stationId);
            if (protocolName) params.set("protocol_name", protocolName);
            if (alertName) params.set("alert_name", alertName);
            if (status) params.set("status", status);
            if (startDate) params.set("start_date", startDate);
            if (endDate) params.set("end_date", endDate);
            const res = await fetch(`${API}/alerts/download?${params.toString()}`, {
                credentials: "include",
            });
            if (!res.ok) throw new Error("Download failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            const bits = ["alerts"];
            if (stationId) bits.push(`station-${stationId}`);
            if (startDate) bits.push(`from-${startDate}`);
            if (endDate) bits.push(`to-${endDate}`);
            a.download = bits.join("_") + ".csv";
            a.click();
            URL.revokeObjectURL(url);
            onClose();
        } catch (e) {
            alert("Download failed — please try again.");
        } finally { setDownloading(false); }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose} data-testid="alerts-download-modal">
            <div className="bg-white rounded-sm w-full max-w-lg" onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-zinc-200 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-zinc-500">Fault Centre · Export</div>
                        <div className="text-sm text-zinc-700 mt-0.5">Download filtered alert history as CSV.</div>
                    </div>
                    <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700" data-testid="alerts-download-close"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-4">
                    <div>
                        <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Plant</label>
                        <select value={stationId} onChange={e => setStationId(e.target.value)} className="w-full h-9 rounded-sm border border-zinc-200 px-3 text-sm bg-white" data-testid="dl-station-select">
                            <option value="">All plants</option>
                            {plants.map(p => <option key={p.id} value={p.id}>{p.customer_name || p.name} · {p.installed_capacity_kw} kWp</option>)}
                        </select>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                            <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Protocol / Code</label>
                            <select value={protocolName} onChange={e => setProtocolName(e.target.value)} className="w-full h-9 rounded-sm border border-zinc-200 px-3 text-sm bg-white" data-testid="dl-protocol-select">
                                {uniqueAlerts.map(p => <option key={p} value={p}>{p || "Any"}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Status</label>
                            <select value={status} onChange={e => setStatus(e.target.value)} className="w-full h-9 rounded-sm border border-zinc-200 px-3 text-sm bg-white" data-testid="dl-status-select">
                                <option value="">Any</option>
                                <option value="active">Active</option>
                                <option value="cleared">Cleared</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Alert name (contains)</label>
                        <Input value={alertName} onChange={e => setAlertName(e.target.value)} placeholder="e.g. UnderFreq" className="rounded-sm" data-testid="dl-name-input" />
                    </div>
                    <div>
                        <label className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-1.5 block">Period</label>
                        <div className="flex flex-wrap gap-1.5 mb-2">
                            {[[7, "7d"], [30, "30d"], [60, "60d"], ["all", "All"]].map(([d, l]) => (
                                <button key={l} onClick={() => applyPreset(d)} className="px-2.5 py-1 text-[10px] uppercase font-mono rounded-sm border border-zinc-200 bg-white hover:bg-zinc-50 text-zinc-600" data-testid={`dl-preset-${l}`}>{l}</button>
                            ))}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <div>
                                <label className="text-[10px] text-zinc-400">From</label>
                                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} max={endDate || undefined} className="w-full h-9 rounded-sm border border-zinc-200 px-2 text-sm bg-white font-mono" data-testid="dl-start-date" />
                            </div>
                            <div>
                                <label className="text-[10px] text-zinc-400">To</label>
                                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} min={startDate || undefined} max={today} className="w-full h-9 rounded-sm border border-zinc-200 px-2 text-sm bg-white font-mono" data-testid="dl-end-date" />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="p-5 border-t border-zinc-200 flex items-center justify-end gap-2">
                    <Button variant="ghost" onClick={onClose} className="rounded-sm">Cancel</Button>
                    <Button onClick={download} disabled={downloading} className="bg-[#0A0A0A] hover:bg-black text-white rounded-full" data-testid="dl-submit-btn">
                        <Download className="w-4 h-4 mr-1.5" /> {downloading ? "Preparing…" : "Download CSV"}
                    </Button>
                </div>
            </div>
        </div>
    );
}
